import React, { Dispatch, SetStateAction, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isEqual } from 'lodash';

import {
  CreateUpdateSalesInvoiceDto,
  GetSalesInvoiceDto,
  GetSalesInvoiceDtoStatusEnum,
  ResponseError,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { SalesInvoiceEditorTabs } from '../../containers/SalesInvoiceEditorView/SalesInvoiceEditorView';
import Button from '../../../common/components/Button';
import { validate } from '../../../common/spdCore/contexts/SalesInvoiceContext';
import { useNavigation } from '../../../common/navigation';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import { useAuth } from '../../../common/hooks/useAuth';
import { Role } from '../../../common/spdCore/authorization/roles';

import { SalesInvoiceCorrectionButton } from './SalesInvoiceCorrectionButton';

interface SalesInvoiceBasicDataControlsProps {
  salesInvoice: CreateUpdateSalesInvoiceDto;
  originalSalesInvoice: CreateUpdateSalesInvoiceDto;
  salesInvoiceId?: string;
  salesInvoiceDto?: GetSalesInvoiceDto;
  setSalesInvoiceId: Dispatch<SetStateAction<string | undefined>>;
  setNotSavedTab: (tab: SalesInvoiceEditorTabs | undefined) => void;
  setSaveErrors: Dispatch<SetStateAction<string[]>>;
  setShowSaveErrors: Dispatch<SetStateAction<boolean>>;
  setOriginalSalesInvoice: Dispatch<
    SetStateAction<CreateUpdateSalesInvoiceDto>
  >;
  setActiveTab: Dispatch<SetStateAction<string>>;
  refreshSalesInvoice: () => Promise<void>;
  readOnly?: boolean;
}

export const SalesInvoiceBasicDataControls: React.FC<
  SalesInvoiceBasicDataControlsProps
> = props => {
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const spd = useSpdCore();
  const nav = useNavigation();
  const auth = useAuth();

  const showCorrectionButton =
    !!props.salesInvoiceDto &&
    auth.check([Role.Admin, Role.Finance]) &&
    !props.salesInvoiceDto.originalInvoice?.id &&
    ((props.salesInvoiceDto.status === GetSalesInvoiceDtoStatusEnum.Approved &&
      props.salesInvoiceDto.toKsef !== true) ||
      props.salesInvoiceDto.status === GetSalesInvoiceDtoStatusEnum.InKSeF);

  const handleSaveAndNext = async () => {
    if (props.readOnly) {
      props.setActiveTab(SalesInvoiceEditorTabs.POSITIONS);
      setSearchParams(searchParams, {
        state: {
          tabKey: SalesInvoiceEditorTabs.POSITIONS,
        },
      });
      return;
    }

    setIsSaveLoading(true);
    props.setSaveErrors([]);
    props.setShowSaveErrors(false);

    try {
      const validationResult = await validate(props.salesInvoice);

      if (!validationResult.valid) {
        props.setSaveErrors(validationResult.errors);
        props.setShowSaveErrors(true);
        setIsSaveLoading(false);
        return;
      }

      const hasChanges = !isEqual(
        props.salesInvoice,
        props.originalSalesInvoice,
      );

      let wasNewInvoice = false;
      if (hasChanges) {
        if (props.salesInvoiceId) {
          await spd.salesInvoices.update(
            props.salesInvoiceId,
            props.salesInvoice,
          );
        } else {
          const savedSalesInvoice = await spd.salesInvoices.create(
            props.salesInvoice,
          );
          props.setSalesInvoiceId(savedSalesInvoice.id);
          void nav.salesInvoiceEditor(savedSalesInvoice.id);
          wasNewInvoice = true;
        }
        props.setOriginalSalesInvoice(props.salesInvoice);
        props.setNotSavedTab(undefined);
        await props.refreshSalesInvoice();
      }

      props.setActiveTab(SalesInvoiceEditorTabs.EXAMS);
      if (!wasNewInvoice) {
        setSearchParams(searchParams, {
          state: {
            tabKey: SalesInvoiceEditorTabs.EXAMS,
          },
        });
      }
    } catch (response) {
      const errors = await responseErrors(response as ResponseError);
      props.setSaveErrors(errors);
      props.setShowSaveErrors(true);
    } finally {
      setIsSaveLoading(false);
    }
  };

  return (
    <div className="ms-auto d-flex gap-2">
      {showCorrectionButton && props.salesInvoiceDto && (
        <SalesInvoiceCorrectionButton invoice={props.salesInvoiceDto} />
      )}
      <Button
        variant="primary"
        onClick={handleSaveAndNext}
        isLoading={isSaveLoading}
      >
        {props.readOnly ? 'Dalej' : 'Zapisz i przejdź dalej'}
      </Button>
    </div>
  );
};
